import { DateTime } from "luxon";
import React, {
  FunctionComponent,
  createContext,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import {
  DietPreference,
  Ethnicity,
  Gender,
  useUpdateUserInformationMutation,
} from "../../../../../api/smartmelts-api/graphql/generated";
import { SnackbarSeverity, useSnackbar } from "../../../../../components/snackbar/snackbar-context";
import { useUser } from "../../../../../components/user/user-context";
import { useEnum } from "../../../../../hooks/use-enum";
import lodash from "lodash";
import { useTranslation } from "react-i18next";
import { useGraphQL } from "../../../../../api/context/graphql-context";

interface IUserDataProviderProps {}

interface IUserDataContext {
  setFirstname: Dispatch<SetStateAction<string>>;
  firstname: string;
  setLastname: Dispatch<SetStateAction<string>>;
  lastname: string;
  setGender: (newValue: Gender) => void;
  gender: Gender;
  setWeight: Dispatch<SetStateAction<number | null>>;
  weight: number | null;
  setDateOfBirth: Dispatch<SetStateAction<string>>;
  dateOfBirth: string;
  setHeight: Dispatch<SetStateAction<number | null>>;
  height: number | null;
  ethnicity: Ethnicity;
  setEthnicity: (newValue: Ethnicity) => void;
  dietPreference: DietPreference;
  setDietPreference: (newValue: DietPreference) => void;
  genderValid: boolean;
  ethnicityValid: boolean;
  dietPreferenceValid: boolean;
  weightValid: boolean;
  heightValid: boolean;
  dateOfBirthDate: DateTime;
  allInputsValid: boolean;
  email: string | null;
  updateUserInformation: () => void;
  updateUserInformationLoading: boolean;
  hasChanges: boolean;
}

export const UserDataContext = createContext<IUserDataContext>({} as IUserDataContext);

export const UserDataContextProvider: FunctionComponent<IUserDataProviderProps> = (props) => {
  const { children } = props;
  const { client } = useGraphQL();
  const { userInformation, fetchUserInformation } = useUser();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [firstname, setFirstname] = useState(userInformation?.firstname || "");
  const [lastname, setLastname] = useState(userInformation?.lastname || "");

  const [gender, setGender, genderValid] = useEnum<Gender>(userInformation?.gender || Gender.MALE, Gender);

  const userWeight = !isNaN(Number(userInformation?.weight)) ? Number(userInformation?.weight) : null;
  const userHeight = !isNaN(Number(userInformation?.height)) ? Number(userInformation?.height) : null;
  const [weight, setWeight] = useState<number | null>(userWeight);
  const [height, setHeight] = useState<number | null>(userHeight);

  const [dateOfBirth, setDateOfBirth] = useState<string>(DateTime.fromISO(userInformation?.dateOfBirth).toISODate());
  const [ethnicity, setEthnicity, ethnicityValid] = useEnum<Ethnicity>(
    userInformation?.ethnicity || Ethnicity.EUROPEAN,
    Ethnicity,
  );
  const [dietPreference, setDietPreference, dietPreferenceValid] = useEnum<DietPreference>(
    userInformation?.dietPreference || DietPreference.STANDARD,
    DietPreference,
  );
  const dateOfBirthDate = useMemo(() => DateTime.fromISO(dateOfBirth), [dateOfBirth]);
  const weightValid = useMemo(() => !lodash.isNaN(weight) && weight! > 0, [weight]);
  const heightValid = useMemo(() => !lodash.isNaN(height) && height! > 0, [height]);

  const allInputsValid = useMemo(
    () => dateOfBirthDate.isValid && ethnicityValid && weightValid && heightValid && dietPreferenceValid,
    [dateOfBirthDate, ethnicityValid, weightValid, heightValid, dietPreferenceValid],
  );

  const email = userInformation?.email ?? null;

  const [updateUserInformationMutation, { loading: updateUserInformationLoading }] = useUpdateUserInformationMutation({
    onCompleted: () => {
      fetchUserInformation();
      client.cache.reset();
      showSnackbar(t("user_data.messages.information_updated"), SnackbarSeverity.SUCCESS);
    },
    onError: () => {
      showSnackbar("An error occured", SnackbarSeverity.ERROR);
    },
  });

  const updateUserInformation = useCallback(() => {
    if (allInputsValid) {
      updateUserInformationMutation({
        variables: {
          firstname,
          lastname,
          gender,
          weight: `${weight}`,
          dateOfBirth,
          height: `${height}`,
          ethnicity,
          dietPreference,
        },
      });
    }
  }, [
    updateUserInformationMutation,
    firstname,
    lastname,
    gender,
    weight,
    dateOfBirth,
    height,
    ethnicity,
    dietPreference,
    allInputsValid,
  ]);

  const hasChanges = useMemo(() => {
    const conditions = [
      userInformation?.firstname === firstname,
      userInformation?.lastname === lastname,
      userInformation?.gender === gender,
      DateTime.fromISO(userInformation?.dateOfBirth).toISODate() === dateOfBirth,
      userInformation?.weight === `${weight}`,
      userInformation?.height === `${height}`,
      userInformation?.ethnicity === ethnicity,
      userInformation?.dietPreference === dietPreference,
    ];
    return lodash.some(conditions, (condition) => !condition);
  }, [firstname, lastname, gender, dateOfBirth, weight, height, ethnicity, dietPreference, userInformation]);

  const value = {
    setFirstname,
    setLastname,
    setGender,
    setEthnicity,
    setWeight,
    setDateOfBirth,
    setHeight,
    firstname,
    lastname,
    gender,
    ethnicityValid,
    genderValid,
    weightValid,
    heightValid,
    weight,
    dateOfBirth,
    height,
    ethnicity,
    dateOfBirthDate,
    allInputsValid,
    dietPreference,
    dietPreferenceValid,
    setDietPreference,
    email,
    updateUserInformation,
    updateUserInformationLoading,
    hasChanges,
  };
  return <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>;
};
